import {
  useFavoritesChangeLocale,
  useFavoritesEmpty,
  useFavoritesGetById,
  useFavoritesGetMyIdAndVersion,
  WebshopFavoritesEvent,
  WebshopFavoritesProduct,
  WebshopFavoritesQueries,
  WebshopFavoritesVersioned,
  WebshopProduct,
} from '@cldsystems/ws-api-client';
import { useQueryClient } from '@tanstack/react-query';
import useAccountLocale from '@/hooks/use-account-locale';
import { useAppContext } from '@/providers/app-provider/app-provider';
import { useKeycloak } from '@/providers/keycloak-provider';
import { FavoritePendingOperation } from '@/store/zustand/favorites.slice';
import { useStore } from '@/store/zustand/zustand.setup';
import { FavoritesActionButtonsEnum } from '@/types/action-buttons/action-buttons';
import { LocalFavorites } from '@/types/favorites/local-favorites';
import { findProductsBySkuAndLocale } from '@/utils/find-products-by-sku-and-locale';
import { getActualVersionFromError, isOptimisticLockError } from '@/utils/get-api-error-message';
import { mapProductToSimpleProductInfo } from '@/utils/map-product-to-simple-product-info';
import toConsentAwareTrackingEvent from '@/utils/to-consent-aware-tracking-event';
import useFavoritesAddProductMutate from './use-favorites-add-product-mutate';
import useFavoritesRemoveProductMutate from './use-favorites-remove-product-mutate';
import useFavoritesSyncQuery from './use-favorites-sync-query';

type FavoriteMutationAction = 'add' | 'remove';

type QueuedFavoriteOperation = {
  action: FavoriteMutationAction;
  favoritesId: string;
  product: WebshopFavoritesProduct;
  event: WebshopFavoritesEvent;
  buttonType?: FavoritesActionButtonsEnum;
  operationId: string;
  productWasInFavorites: boolean;
  previousProduct?: WebshopFavoritesProduct;
};

let favoriteOperationCounter = 0;
const favoriteOperationQueue: QueuedFavoriteOperation[] = [];
let isProcessingFavoriteOperationQueue = false;

type UseFavorites = {
  favorites: LocalFavorites;
  addProductToFavorites: (
    product: WebshopFavoritesProduct,
    event: WebshopFavoritesEvent,
    buttonType?: FavoritesActionButtonsEnum,
  ) => void;
  removeProductFromFavorites: (
    product: WebshopFavoritesProduct,
    event: WebshopFavoritesEvent,
    buttonType?: FavoritesActionButtonsEnum,
  ) => void;
  getFavoritePending: (productId: string) => FavoritePendingOperation | undefined;
  isFavoritePending: (productId: string, buttonType?: FavoritesActionButtonsEnum) => boolean;
  emptyFavorites: () => Promise<void>;
  changeFavoritesLocale: (newLocale: string) => Promise<void>;
  isLoadingFavorites: boolean;
};

const useFavorites = (): UseFavorites => {
  const { isAuthenticated, isInitialized } = useKeycloak();
  const { locale: accountLocale, isLoading: isAccountLocaleLoading } = useAccountLocale();
  const appContext = useAppContext<{ allProducts?: { items: WebshopProduct[] } } | undefined>();
  const allProducts = appContext?.allProducts?.items ?? [];
  // NOTE: post-deploy reconciliation lives in `<DeploymentVersionReconciler />`
  // (mounted from `<Page />`), NOT here. See `useMyCart` for the rationale.

  const queryClient = useQueryClient();
  const {
    favorites,
    setFavourites,
    addProductToFavorites: addProductToFavoritesLocal,
    removeProductFromFavorites: removeProductFromFavoritesLocal,
    resetFavorites,
    markFavoritePending,
    clearFavoritePending,
    getFavoritePending,
    isFavoritePending,
  } = useStore();

  const guestProductsForFavoritesMerge = (() => {
    if (!isAuthenticated || !accountLocale || favorites.products.length === 0) {
      return favorites.products;
    }

    const needsReconciliation = favorites.products.some(
      product => product.locale?.toLowerCase() !== accountLocale,
    );

    if (!needsReconciliation) return favorites.products;

    const skus = favorites.products.map(product => product.sku);
    const matches = findProductsBySkuAndLocale(allProducts, skus, accountLocale);

    if (matches.length === 0) return favorites.products;

    const matchBySku = new Map(
      matches.map(match => [match.sku, mapProductToSimpleProductInfo(match)]),
    );

    return favorites.products
      .map(product => matchBySku.get(product.sku))
      .filter((product): product is WebshopFavoritesProduct => product != null);
  })();

  const favoritesGetMyIdAndVersion = useFavoritesGetMyIdAndVersion({
    enabled: isAuthenticated,
  });
  const favoritesGetById = useFavoritesGetById({
    params: { id: favoritesGetMyIdAndVersion.data?.id },
    enabled:
      isAuthenticated &&
      favoritesGetMyIdAndVersion.isSuccess &&
      !!favoritesGetMyIdAndVersion.data.id,
  });

  const createFavoriteOperationId = () => {
    favoriteOperationCounter += 1;

    return `favorite-operation-${favoriteOperationCounter}`;
  };

  const getFavoritesQueryKey = (favoritesId: string) =>
    [WebshopFavoritesQueries.GetById, favoritesId] as const;

  /**
   * Authoritative favorites version for the next mutation. Read from the GetById
   * cache (which every mutation's `onMutate` bumps optimistically) so serialized
   * writes send incrementing versions without waiting for a refetch.
   */
  const getCurrentFavoritesVersion = (favoritesId: string): number | undefined => {
    const cached = queryClient.getQueryData<WebshopFavoritesVersioned>(
      getFavoritesQueryKey(favoritesId),
    );

    return cached?.version ?? favoritesGetById.data?.version;
  };

  /**
   * Run a favorites mutation and self-heal optimistic-lock conflicts: on a
   * version conflict (code 3) reconcile to the server's actual version and
   * replay once. The first attempt suppresses the error toast; the replay
   * surfaces it, so the user is only notified when it can't auto-resolve.
   */
  const runFavoriteWithVersionRetry = async (
    favoritesId: string,
    productId: string,
    operationId: string,
    mutate: (version: string, suppressVersionConflictToast: boolean) => Promise<unknown>,
  ): Promise<void> => {
    const currentVersion = getCurrentFavoritesVersion(favoritesId);

    if (currentVersion == null) {
      clearFavoritePending(productId, operationId);
      return;
    }

    try {
      await mutate(currentVersion.toString(), true);
    } catch (error) {
      if (!isOptimisticLockError(error)) {
        throw error;
      }

      let actualVersion = getActualVersionFromError(error);

      if (actualVersion == null) {
        await queryClient.invalidateQueries({ queryKey: getFavoritesQueryKey(favoritesId) });
        actualVersion = getCurrentFavoritesVersion(favoritesId);
      }

      if (actualVersion == null) {
        throw error;
      }

      const resolvedVersion = actualVersion;
      queryClient.setQueryData<WebshopFavoritesVersioned>(getFavoritesQueryKey(favoritesId), old =>
        old ? { ...old, version: resolvedVersion } : old,
      );

      await mutate(resolvedVersion.toString(), false);
    }
  };

  const applyOptimisticFavoriteOperation = (operation: QueuedFavoriteOperation) => {
    queryClient.setQueryData<WebshopFavoritesVersioned>(
      getFavoritesQueryKey(operation.favoritesId),
      old => {
        const currentFavorites = old ?? favoritesGetById.data;

        if (!currentFavorites) {
          return old;
        }

        const products =
          operation.action === 'add'
            ? currentFavorites.favorites.products.some(
                product => product.id === operation.product.id,
              )
              ? currentFavorites.favorites.products
              : [...currentFavorites.favorites.products, operation.product]
            : currentFavorites.favorites.products.filter(
                product => product.id !== operation.product.id,
              );

        return {
          ...currentFavorites,
          favorites: {
            ...currentFavorites.favorites,
            products,
          },
        };
      },
    );
  };

  const enqueueFavoriteOperation = (operation: QueuedFavoriteOperation) => {
    const queuedOperationIndex = favoriteOperationQueue.findIndex(
      queuedOperation => queuedOperation.product.id === operation.product.id,
    );

    if (queuedOperationIndex === -1) {
      favoriteOperationQueue.push(operation);
      return;
    }

    favoriteOperationQueue.splice(queuedOperationIndex, 1, operation);
  };

  const refreshFavoriteQueries = async (favoritesId: string) => {
    // Skip while more writes are queued/in flight so a late refetch can't clobber
    // the optimistic version mid-burst (which would make the next write send a
    // stale version -> optimistic-lock failure). The follow-up burst reconciles.
    if (favoriteOperationQueue.length > 0 || isProcessingFavoriteOperationQueue) {
      return;
    }

    await queryClient.invalidateQueries({ queryKey: [WebshopFavoritesQueries.GetList] });
    await queryClient.invalidateQueries({ queryKey: getFavoritesQueryKey(favoritesId) });
  };

  const favoritesAddProduct = useFavoritesAddProductMutate(
    markFavoritePending,
    clearFavoritePending,
    getFavoritePending,
  );
  const favoritesRemoveProduct = useFavoritesRemoveProductMutate(
    markFavoritePending,
    clearFavoritePending,
    getFavoritePending,
  );

  const processFavoriteOperationQueue = async () => {
    if (isProcessingFavoriteOperationQueue) {
      return;
    }

    isProcessingFavoriteOperationQueue = true;
    let processedFavoritesId: string | undefined;

    try {
      while (favoriteOperationQueue.length > 0) {
        const operation = favoriteOperationQueue.shift();

        if (!operation) {
          continue;
        }

        processedFavoritesId = operation.favoritesId;

        try {
          await runFavoriteWithVersionRetry(
            operation.favoritesId,
            operation.product.id!,
            operation.operationId,
            (version, suppressVersionConflictToast) =>
              operation.action === 'add'
                ? favoritesAddProduct.mutateAsync({
                    params: {
                      id: operation.favoritesId,
                      version,
                    },
                    payload: {
                      productId: operation.product.id!,
                      event: operation.event,
                      product: operation.product,
                      operationId: operation.operationId,
                      buttonType: operation.buttonType,
                      optimisticApplied: true,
                      productWasInFavorites: operation.productWasInFavorites,
                      suppressVersionConflictToast,
                    },
                  })
                : favoritesRemoveProduct.mutateAsync({
                    params: {
                      id: operation.favoritesId,
                      version,
                    },
                    payload: {
                      productId: operation.product.id!,
                      event: operation.event,
                      product: operation.product,
                      operationId: operation.operationId,
                      buttonType: operation.buttonType,
                      optimisticApplied: true,
                      previousProduct: operation.previousProduct,
                      suppressVersionConflictToast,
                    },
                  }),
          );
        } catch {}
      }
    } finally {
      isProcessingFavoriteOperationQueue = false;

      if (processedFavoritesId) {
        await refreshFavoriteQueries(processedFavoritesId);
      }
    }
  };

  const favoritesEmpty = useFavoritesEmpty();
  const favoritesChangeLocale = useFavoritesChangeLocale();

  const { isSyncing: isFavoritesSyncing } = useFavoritesSyncQuery({
    payload: {
      productIds: guestProductsForFavoritesMerge.map(p => p.id!),
      event: {
        name: '',
        id: '',
        sourceUrl: '',
      },
    },
    createEnabled:
      isAuthenticated &&
      !isAccountLocaleLoading &&
      favoritesGetMyIdAndVersion.isSuccess &&
      !favoritesGetMyIdAndVersion.data?.id,
    addEnabled:
      isAuthenticated &&
      !isAccountLocaleLoading &&
      favoritesGetMyIdAndVersion.isSuccess &&
      !!favoritesGetMyIdAndVersion.data.id &&
      !!favoritesGetMyIdAndVersion.data.version &&
      guestProductsForFavoritesMerge.length !== 0,
    favoritesId: favoritesGetMyIdAndVersion.data?.id,
    favoritesVersion: favoritesGetMyIdAndVersion.data?.version,
  });

  if (isAuthenticated) {
    return {
      favorites: {
        products: favoritesGetById.data?.favorites.products || [],
      },
      isLoadingFavorites:
        isAccountLocaleLoading ||
        favoritesGetMyIdAndVersion.isLoading ||
        isFavoritesSyncing ||
        favoritesGetById.isLoading,
      addProductToFavorites: (product, event, buttonType) => {
        if (!favoritesGetById.isSuccess) return;
        const favoritesId = favoritesGetById.data.favorites.id;
        const currentFavorites = queryClient.getQueryData<WebshopFavoritesVersioned>(
          getFavoritesQueryKey(favoritesId),
        );
        const previousProduct = currentFavorites?.favorites.products.find(
          favoriteProduct => favoriteProduct.id === product.id,
        );
        const consentAwareEvent = toConsentAwareTrackingEvent({
          name: event.name ?? '',
          id: event.id,
          sourceUrl: event.sourceUrl ?? '',
        });
        const operationId = createFavoriteOperationId();
        const operation: QueuedFavoriteOperation = {
          action: 'add',
          favoritesId,
          product,
          event: consentAwareEvent,
          buttonType,
          operationId,
          productWasInFavorites: Boolean(previousProduct),
          previousProduct,
        };

        markFavoritePending(product.id!, 'add', buttonType, operationId);
        applyOptimisticFavoriteOperation(operation);
        enqueueFavoriteOperation(operation);
        void processFavoriteOperationQueue();
      },
      removeProductFromFavorites: (product, event, buttonType) => {
        if (!favoritesGetById.isSuccess) return;
        const favoritesId = favoritesGetById.data.favorites.id;
        const currentFavorites = queryClient.getQueryData<WebshopFavoritesVersioned>(
          getFavoritesQueryKey(favoritesId),
        );
        const previousProduct = currentFavorites?.favorites.products.find(
          favoriteProduct => favoriteProduct.id === product.id,
        );

        // Guard against double-remove: the optimistic update removes the product
        // from the cache synchronously, so a duplicate click finds no
        // `previousProduct` and is ignored (avoids a remove for a product the
        // server no longer has).
        if (!previousProduct) {
          return;
        }

        const consentAwareEvent = toConsentAwareTrackingEvent({
          name: event.name ?? '',
          id: event.id,
          sourceUrl: event.sourceUrl ?? '',
        });
        const operationId = createFavoriteOperationId();
        const operation: QueuedFavoriteOperation = {
          action: 'remove',
          favoritesId,
          product,
          event: consentAwareEvent,
          buttonType,
          operationId,
          productWasInFavorites: Boolean(previousProduct),
          previousProduct,
        };

        markFavoritePending(product.id!, 'remove', buttonType, operationId);
        applyOptimisticFavoriteOperation(operation);
        enqueueFavoriteOperation(operation);
        void processFavoriteOperationQueue();
      },
      emptyFavorites: async () => {
        if (!favoritesGetById.isSuccess || favoritesGetById.data.favorites.products.length === 0)
          return;

        await favoritesEmpty.mutateAsync({
          params: {
            id: favoritesGetById.data.favorites.id,
            version: favoritesGetById.data.version.toString(),
          },
        });
      },
      changeFavoritesLocale: async (newLocale: string) => {
        if (!favoritesGetById.isSuccess || favoritesGetById.data.favorites.products.length === 0)
          return;

        const id = favoritesGetById.data.favorites.id;
        const version = favoritesGetById.data.version;

        try {
          await favoritesChangeLocale.mutateAsync({
            params: { id, version: version.toString() },
            payload: { locale: newLocale },
          });
          await queryClient.invalidateQueries({ queryKey: getFavoritesQueryKey(id) });
        } catch {
          await favoritesEmpty.mutateAsync({ params: { id, version: version.toString() } });
        }
      },
      getFavoritePending,
      isFavoritePending,
    };
  }

  return {
    favorites,
    isLoadingFavorites: !isInitialized,
    addProductToFavorites: addProductToFavoritesLocal,
    removeProductFromFavorites: removeProductFromFavoritesLocal,
    emptyFavorites: async () => {
      resetFavorites();
    },
    changeFavoritesLocale: async (newLocale: string) => {
      if (favorites.products.length === 0) return;

      const skus = favorites.products.map(product => product.sku);
      const matches = findProductsBySkuAndLocale(allProducts, skus, newLocale);

      if (matches.length === 0) {
        resetFavorites();
        return;
      }

      const matchBySku = new Map(
        matches.map(match => [match.sku, mapProductToSimpleProductInfo(match)]),
      );

      const products = favorites.products
        .map(product => matchBySku.get(product.sku))
        .filter((product): product is WebshopFavoritesProduct => product != null);

      setFavourites({ products });
    },
    getFavoritePending,
    isFavoritePending,
  };
};

export default useFavorites;
